import React, { Suspense, useState } from 'react';
import Card from '@/components/card';
import Title from '@/components/title';
import StockTable from './components/stock-table';
import StockAddModal from '../maintain/stock-add-modal';
import { IDataModal } from '@/interfaces/dataModal';
import { IStock, ISearchStockReq, ISearchStockRes } from '@/interfaces/stock';
import {
  Button,
  DatePicker,
  Dropdown,
  Input,
  MenuProps,
  Radio,
  TablePaginationConfig,
} from 'antd';
import {
  PlusOutlined,
  ImportOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import { searchStocks, uploadCoa } from '@/adapters/stock';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { AxiosResponse } from 'axios';
import { useQuery } from '@tanstack/react-query';
import MediaUploader from '@/components/media/uploader-modal';
import StockMovementModal from '@/components/stock-movement/stock-movement-modal';
import StockReservedModal from '@/components/stock/stock-reserved-modal';
import { IImportData } from '@/interfaces/import';
import ImportStockModal from '@/components/import/stock/import-stock-modal';
import ImportCoaModal from '@/components/import/coa/import-coa-modal';
import { isFullAccess, isWareHouse } from '@/utils/permissionUtils';
import BusinessUnitSelector from '@/components/selector/business-unit-selector';
import { Dayjs } from 'dayjs';
import locale from 'antd/es/date-picker/locale/th_TH';

const _stockModal: IDataModal<IStock> = {
  open: false,
};
const _importModal: IDataModal<IImportData> = {
  open: false,
};
const { Search } = Input;

const _criteria: ISearchStockReq = {
  orderBy: 'id',
  pageSize: 10,
  pageStart: 1,
  search: '',
  isActive: true,
};

const Stock = () => {
  const perFullAccess = isFullAccess();
  const perWareHouse = isWareHouse();
  const [criteria, setCriteria] = useState<ISearchStockReq>({
    ..._criteria,
  });
  const [dataModal, setDataModal] = useState<IDataModal<IStock>>(_stockModal);
  const [importModal, setImportModal] =
    useState<IDataModal<IImportData>>(_importModal);
  const [updateModal, setUploadModal] =
    useState<IDataModal<IStock>>(_stockModal);
  const [movementModal, setMovementModal] =
    useState<IDataModal<IStock>>(_stockModal);
  const [reservedModal, setReservedModal] =
    useState<IDataModal<IStock>>(_stockModal);

  const {
    data: stock,
    isLoading,
    refetch,
    isRefetching,
  } = useQuery<AxiosResponse<ISearchStockRes>, Error>({
    queryKey: ['search-stock', criteria],
    queryFn: async () => await searchStocks(criteria),
  });

  const onActionStock = () => {
    refetch();
    setDataModal({
      ..._stockModal,
    });
  };

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IStock> | SorterResult<IStock>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy = (sorter.column?.dataIndex as keyof IStock) || '';
      newCriteria.orderType = sorter.order || '';
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const onSearch = (text: string) => {
    setCriteria({
      ...criteria,
      search: text,
      pageStart: 1,
      pageSize: 10,
    });
  };

  const onChange = (value: 'product' | 'sample' | 'equipment') => {
    let iSample: boolean | undefined = false;
    let isEquipment: boolean | undefined = false;
    let isActive: boolean | undefined = true;
    switch (value) {
      case 'product':
        iSample = false;
        isEquipment = false;
        break;
      case 'sample':
        iSample = true;
        isEquipment = false;
        isActive = undefined;
        break;
      case 'equipment':
        iSample = true;
        isEquipment = true;
        isActive = undefined;
        break;
      default:
        break;
    }

    setCriteria({
      ...criteria,
      search: '',
      orderBy: 'id',
      orderType: '',
      pageStart: 1,
      pageSize: 10,
      iSample,
      isEquipment,
      isActive: isActive,
    });
  };

  const onRangeChange = (dates: null | (Dayjs | null)[]) => {
    if (dates) {
      setCriteria({
        ...criteria,
        orderBy: 'id',
        orderType: '',
        pageStart: 1,
        pageSize: 10,
        startDate: dates[0]?.toDate(),
        endDate: dates[1]?.toDate(),
      });
    } else {
      setCriteria({
        ...criteria,
        orderBy: 'id',
        orderType: '',
        pageStart: 1,
        pageSize: 10,
        startDate: null,
        endDate: null,
      });
    }
  };

  const handleMenuClick: MenuProps['onClick'] = e => {
    switch (e.key) {
      case '1':
        setCriteria({
          ...criteria,
          isActive: undefined,
          pageStart: 1,
          type: e.key,
        });
        break;
      case '2':
        setCriteria({
          ...criteria,
          isActive: true,
          pageStart: 1,
          type: e.key,
        });
        break;
      case '3':
        setCriteria({
          ...criteria,
          isActive: false,
          pageStart: 1,
          type: e.key,
        });
        break;
        break;
      default:
        break;
    }
  };

  const items: MenuProps['items'] = [
    {
      label: 'ทั้งหมด',
      key: '1',
    },
    {
      label: 'ยังไม่ได้รับ',
      key: '2',
    },
    {
      label: 'รับแล้ว',
      key: '3',
    },
  ];

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  return (
    <Card>
      <Title title="จัดการสต็อก">
        <div className="flex flex-col justify-end md:flex-row gap-2">
          {(perFullAccess || perWareHouse) && (
            <>
              <Button
                ghost
                type="primary"
                onClick={() => {
                  setImportModal({
                    open: true,
                    id: 'coa',
                  });
                }}
              >
                <ImportOutlined />
                นำเข้าข้อมูล COA
              </Button>
              <Button
                ghost
                type="primary"
                onClick={() => {
                  setImportModal({
                    open: true,
                    id: 'stock',
                  });
                }}
              >
                <ImportOutlined />
                นำเข้าข้อมูล Stock
              </Button>
              <Button
                type="primary"
                className="max-w-[100px]"
                onClick={() => {
                  setDataModal({
                    ...dataModal,
                    open: true,
                  });
                }}
              >
                <PlusOutlined /> เพิ่มใหม่
              </Button>
            </>
          )}
        </div>
      </Title>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col md:flex-row gap-3">
          <Radio.Group
            size="large"
            onChange={e => onChange(e.target.value)}
            buttonStyle="solid"
            defaultValue={'product'}
          >
            <Radio.Button value={'product'}>
              <div className="flex items-center gap-1">สินค้า</div>
            </Radio.Button>
            <Radio.Button value={'sample'}>
              <div className="flex items-center gap-1">ตัวอย่าง</div>
            </Radio.Button>
            <Radio.Button value={'equipment'}>
              <div className="flex items-center gap-1">อุปกรณ์</div>
            </Radio.Button>
          </Radio.Group>
          {criteria?.iSample && (
            <>
              <DatePicker.RangePicker
                size="large"
                locale={locale}
                onChange={onRangeChange}
                // defaultValue={[
                //   dayjs(criteria?.startDate as string),
                //   dayjs(criteria?.endDate as string),
                // ]}
              />
              <BusinessUnitSelector
                handleSelectData={function (id: number): void {
                  setCriteria({ ...criteria, businessUnitId: id });
                }}
              />
              <Dropdown.Button
                className="w-[140px]"
                size="large"
                menu={menuProps}
                placement="bottom"
                icon={<FilterOutlined />}
              >
                {criteria.type
                  ? criteria.type === '1'
                    ? 'ทั้งหมด'
                    : criteria.type === '2'
                      ? 'ยังไม่ได้รับ'
                      : 'รับแล้ว'
                  : 'สถานะ'}
              </Dropdown.Button>
            </>
          )}
          <Search
            className="md:max-w-[400px] md:min-w-[400px]"
            placeholder="ค้นหา"
            allowClear
            size="large"
            enterButton="ค้นหา"
            onSearch={onSearch}
          />
        </div>
        <StockTable
          criteria={criteria}
          stock={stock?.data || { data: [], totalRecords: 0 }}
          loading={isLoading || isRefetching}
          onEditData={item =>
            setDataModal({ ..._stockModal, id: item.id, item, open: true })
          }
          onDelete={onActionStock}
          onTableChange={onTableChange}
          onUploadCoa={id => {
            setUploadModal({
              open: true,
              id: id,
            });
          }}
          onShowStockMovement={id => {
            setMovementModal({
              open: true,
              id: id,
            });
          }}
          onShowStockReserved={item => {
            setReservedModal({
              open: true,
              id: item.id,
              item,
            });
          }}
        />
      </div>
      <MediaUploader
        data={{
          modelId: Number(updateModal?.id),
          modelType: 'coa',
        }}
        open={updateModal.open}
        onCancel={() => {
          setUploadModal({
            ..._stockModal,
          });
        }}
        onUploaded={() => {
          setUploadModal({
            ..._stockModal,
          });
          refetch();
        }}
        ExternalApi={data => uploadCoa(data)}
      />
      <StockAddModal
        handleCancel={() => setDataModal({ ..._stockModal })}
        handleOk={() => onActionStock()}
        dataModal={dataModal}
      />
      <Suspense>
        {movementModal.id && (
          <StockMovementModal
            handleCancel={() => setMovementModal({ ..._stockModal })}
            open={movementModal.open}
            stockId={Number(movementModal.id)}
          />
        )}
        {reservedModal.open && reservedModal.item && (
          <StockReservedModal
            handleCancel={() => setReservedModal({ ..._stockModal })}
            open={reservedModal.open}
            stock={reservedModal.item}
          />
        )}
        {importModal?.open && importModal?.id === 'stock' && (
          <ImportStockModal
            open={importModal?.open}
            handleOk={function (): void {
              setImportModal({
                ..._importModal,
              });
            }}
            handleCancel={function (): void {
              setImportModal({
                ..._importModal,
              });
            }}
          />
        )}
        {importModal?.open && importModal?.id === 'coa' && (
          <ImportCoaModal
            open={importModal?.open}
            handleOk={function (): void {
              setImportModal({
                ..._importModal,
              });
            }}
            handleCancel={function (): void {
              setImportModal({
                ..._importModal,
              });
            }}
          />
        )}
      </Suspense>
    </Card>
  );
};

export default Stock;
